import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { cn } from '@/shared/utils';
import {
  Play,
  CheckCircle,
  MapPin,
  ArrowRight,
  AlertTriangle,
  Gauge,
  Thermometer,
  ThermometerSnowflake,
  GitBranch,
  Shield,
  Clock,
  User,
  FileText,
  Edit,
  Trash2,
  Camera,
  RefreshCw } from
'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { ScrollArea } from '@/shared/components/ui/scroll-area';

// Event type configurations with Uber-inspired styling
const eventConfig = {
  // GPS Events
  'START': {
    icon: Play,
    color: 'emerald',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
    label: 'Route Started'
  },
  'END': {
    icon: CheckCircle,
    color: 'emerald',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
    label: 'Route Complete'
  },
  'STOP_ARRIVAL': {
    icon: MapPin,
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    label: 'Arrived at Stop'
  },
  'STOP_DEPARTURE': {
    icon: ArrowRight,
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    label: 'Departed Stop'
  },
  'UNSCHEDULED_STOP': {
    icon: AlertTriangle,
    color: 'amber',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    label: 'Unscheduled Stop'
  },
  'SPEED_WARNING': {
    icon: Gauge,
    color: 'orange',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200',
    label: 'Speed Warning'
  },
  'TEMP_EXCURSION': {
    icon: Thermometer,
    color: 'red',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    label: 'Temperature Excursion'
  },
  'TEMP_RECOVERED': {
    icon: ThermometerSnowflake,
    color: 'emerald',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
    label: 'Temperature Recovered'
  },
  'DEVIATION': {
    icon: GitBranch,
    color: 'violet',
    bgColor: 'bg-violet-50',
    textColor: 'text-violet-700',
    borderColor: 'border-violet-200',
    label: 'Route Deviation'
  },
  'GEOFENCE_BREACH': {
    icon: Shield,
    color: 'red',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    label: 'Geofence Breach'
  },
  // Audit Events
  'CREATE': {
    icon: FileText,
    color: 'emerald',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
    label: 'Created'
  },
  'EDIT': {
    icon: Edit,
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    label: 'Edited'
  },
  'DELETE': {
    icon: Trash2,
    color: 'red',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    label: 'Deleted'
  },
  'STATUS_CHANGE': {
    icon: RefreshCw,
    color: 'violet',
    bgColor: 'bg-violet-50',
    textColor: 'text-violet-700',
    borderColor: 'border-violet-200',
    label: 'Status Changed'
  },
  'POD_CAPTURE': {
    icon: Camera,
    color: 'cyan',
    bgColor: 'bg-cyan-50',
    textColor: 'text-cyan-700',
    borderColor: 'border-cyan-200',
    label: 'POD Captured'
  },
  'ALERT': {
    icon: AlertTriangle,
    color: 'amber',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    label: 'Alert'
  },
  'REROUTE': {
    icon: GitBranch,
    color: 'orange',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200',
    label: 'Rerouted'
  },
  'CLOCK_IN': {
    icon: Clock,
    color: 'green',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
    label: 'Clocked In'
  },
  'CLOCK_OUT': {
    icon: Clock,
    color: 'gray',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
    label: 'Clocked Out'
  }
};

const getEventConfig = (type) => {
  return eventConfig[type] || {
    icon: Clock,
    color: 'gray',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
    label: type
  };
};

// Single timeline event item - Uber style card
const TimelineEvent = ({ event, isActive, isPast, isLast, onClick }) => {const { t } = useTranslation("ezparcel");
  const config = getEventConfig(event.event || event.action);
  const Icon = config.icon;
  const time = event.timestamp ?
  new Date(event.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
  '--:--';

  return (
    <div
      className={cn(
        "relative flex gap-3 cursor-pointer group",
        !isLast && "pb-6"
      )}
      onClick={() => onClick?.(event)}
      data-testid={`timeline-event-${event.id}`}>
      
      {/* Timeline connector line */}
      {!isLast &&
      <div className={cn(
        "absolute left-[19px] top-10 w-0.5 h-[calc(100%-24px)]",
        isPast ? "bg-emerald-300" : "bg-gray-200"
      )} />
      }
      
      {/* Event icon */}
      <div className={cn(
        "relative z-10 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-200",
        isActive ? "ring-4 ring-blue-100 scale-110" : "",
        isPast ? config.bgColor + " " + config.borderColor : "bg-white border-gray-200",
        "group-hover:scale-105"
      )}>
        <Icon className={cn(
          "h-4 w-4",
          isPast ? config.textColor : "text-gray-400"
        )} />
      </div>
      
      {/* Event content */}
      <div className={cn(
        "flex-1 rounded-lg border p-3 transition-all duration-200",
        isActive ? "border-blue-300 bg-blue-50/50 shadow-md" : "border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm"
      )}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn(
                "text-sm font-medium",
                isPast ? "text-gray-900" : "text-gray-500"
              )}>
                {config.label}
              </span>
              {event.severity &&
              <Badge variant={
              event.severity === 'Critical' ? 'destructive' :
              event.severity === 'Warning' ? 'outline' : 'secondary'
              } className="text-[10px] h-5">
                  {event.severity}
                </Badge>
              }
            </div>
            
            {/* Location or description */}
            {(event.location || event.description) &&
            <p className="text-xs text-gray-500 mt-1 truncate">
                {event.location || event.description}
              </p>
            }
            
            {/* Additional info for audit events */}
            {event.user &&
            <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                <User className="h-3 w-3" />
                <span>{event.user}</span>
              </div>
            }
            
            {/* Temperature info */}
            {event.temp !== undefined &&
            <div className={cn(
              "flex items-center gap-1 mt-1 text-xs",
              event.temp > 8 ? "text-red-600" : "text-emerald-600"
            )}>
                <Thermometer className="h-3 w-3" />
                <span>{event.temp}{t("c", "\xB0C")}</span>
              </div>
            }
            
            {/* POD captured indicator */}
            {event.podCaptured &&
            <div className="flex items-center gap-1 mt-1 text-xs text-cyan-600">
                <Camera className="h-3 w-3" />
                <span>{t("podCaptured", "POD Captured")}</span>
              </div>
            }
          </div>
          
          {/* Timestamp */}
          <div className="text-xs text-gray-400 whitespace-nowrap font-mono">
            {time}
          </div>
        </div>
        
        {/* Speed info if available */}
        {event.speed !== undefined && event.speed > 0 &&
        <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Gauge className="h-3 w-3" />
              {event.speed}{t("kmh", "km/h")}
          </span>
          </div>
        }
      </div>
    </div>);

};

export const TrailEventTimeline = ({
  events = [],
  currentIndex = 0,
  onEventClick,
  maxHeight = 500,
  showFilters = false,
  title = "Event Timeline"
}) => {const { t } = useTranslation("ezparcel");
  const [filter, setFilter] = useState('all');

  // Filter events
  const filteredEvents = events.filter((e) => {
    if (filter === 'all') return true;
    if (filter === 'stops') return ['STOP_ARRIVAL', 'STOP_DEPARTURE', 'START', 'END'].includes(e.event);
    if (filter === 'alerts') return ['TEMP_EXCURSION', 'UNSCHEDULED_STOP', 'SPEED_WARNING', 'ALERT'].includes(e.event || e.action);
    return true;
  });

  const filterOptions = [
  { value: 'all', label: 'All Events' },
  { value: 'stops', label: 'Stops Only' },
  { value: 'alerts', label: 'Alerts Only' }];


  return (
    <div className="bg-white rounded-xl border border-gray-200" data-testid="trail-event-timeline">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="font-mono">
            {filteredEvents.length}{t("events", "events")}
          </Badge>
        </div>
      </div>
      
      {/* Filters */}
      {showFilters &&
      <div className="flex items-center gap-2 p-3 border-b border-gray-100 bg-gray-50/50">
          {filterOptions.map((option) =>
        <button
          key={option.value}
          onClick={() => setFilter(option.value)}
          className={cn(
            "px-3 py-1.5 text-xs font-medium rounded-full transition-colors",
            filter === option.value ?
            "bg-gray-900 text-white" :
            "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
          )}>
          
              {option.label}
            </button>
        )}
        </div>
      }
      
      {/* Timeline */}
      <ScrollArea className="p-4" style={{ maxHeight: `${maxHeight}px` }}>
        {filteredEvents.length > 0 ?
        <div>
            {filteredEvents.map((event, idx) =>
          <TimelineEvent
            key={event.id || idx}
            event={event}
            isActive={idx === currentIndex}
            isPast={idx <= currentIndex}
            isLast={idx === filteredEvents.length - 1}
            onClick={onEventClick} />

          )}
          </div> :

        <div className="text-center py-8 text-gray-500">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t("noEventsTo", "No events to display")}</p>
          </div>
        }
      </ScrollArea>
    </div>);

};